import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ImageUploader from '../../components/admin/ImageUploader';
import LucideIconPicker from '../../components/admin/LucideIconPicker';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import * as LucideIcons from 'lucide-react';
import { sectionApi, pageApi } from '../../services/api';

/* ============================================================
   SECTION TYPE DEFINITIONS
   ============================================================ */
const ALL_TYPES = [
  {
    key: 'hero', label: 'Hero', icon: '🎯',
    desc: 'Bagian paling atas dengan judul besar dan tombol CTA.',
    tip: 'Judul singkat (maks 8 kata). Subjudul jelaskan value. Sertakan 1-2 tombol.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Kebersihan Premium' },
      { key: 'subtitle', label: 'Subjudul', placeholder: 'Solusi pembersihan profesional...' },
      { key: 'badge_text', label: 'Teks Badge', placeholder: 'contoh: 💧 Teknologi Hydro Vacuum' },
      { key: 'cta_text', label: 'Teks Tombol Utama', placeholder: 'contoh: Pesan Sekarang' },
      { key: 'cta_link', label: 'Link Tombol Utama', placeholder: 'contoh: /order' },
      { key: 'cta_secondary_text', label: 'Teks Tombol Kedua', placeholder: 'contoh: Pelajari Lebih Lanjut' },
      { key: 'cta_secondary_link', label: 'Link Tombol Kedua', placeholder: 'contoh: /about' },
    ],
    defaultContent: { title: '', subtitle: '', badge_text: '', cta_text: '', cta_link: '', cta_secondary_text: '', cta_secondary_link: '' },
  },
  {
    key: 'features', label: 'Fitur', icon: '✨',
    desc: 'Grid card keunggulan. Cocok untuk Beranda.',
    tip: '3-4 fitur dengan icon, judul, deskripsi singkat.',
    fields: [
      { key: 'title', label: 'Judul Bagian', placeholder: 'contoh: Kenapa Memilih Kami?' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
    ],
    arrayField: {
      key: 'features', label: 'Fitur', singular: 'fitur',
      fields: [
        { key: 'icon', label: 'Icon', type: 'icon' },
        { key: 'title', label: 'Judul Fitur', placeholder: 'contoh: Hydro Vacuum Technology' },
        { key: 'description', label: 'Deskripsi', placeholder: 'Jelaskan fitur ini dalam 1-2 kalimat', type: 'textarea' },
      ],
    },
    defaultContent: { title: '', subtitle: '', features: [] },
  },
  {
    key: 'services', label: 'Layanan', icon: '🧹',
    desc: 'Daftar layanan/jasa dengan detail lengkap.',
    tip: 'Setiap layanan: nama, deskripsi, harga, badge, gambar.',
    fields: [
      { key: 'title', label: 'Judul Bagian', placeholder: 'contoh: Layanan Unggulan Kami' },
      { key: 'subtitle', label: 'Subjudul', placeholder: 'Dari rumah hingga kendaraan...' },
    ],
    arrayField: {
      key: 'services', label: 'Layanan', singular: 'layanan',
      fields: [
        { key: 'name', label: 'Nama Layanan', placeholder: 'contoh: Deep Cleaning Rumah' },
        { key: 'slug', label: 'Slug URL', placeholder: 'contoh: deep-cleaning-rumah' },
        { key: 'icon', label: 'Icon', type: 'icon' },
        { key: 'badge', label: 'Badge', placeholder: 'contoh: Best Seller' },
        { key: 'short_description', label: 'Deskripsi Singkat', placeholder: '1 kalimat', type: 'textarea' },
        { key: 'description', label: 'Deskripsi Lengkap', placeholder: 'Paragraf penjelasan', type: 'textarea' },
        { key: 'price', label: 'Harga', placeholder: 'contoh: Rp 350.000' },
        { key: 'price_unit', label: 'Satuan', placeholder: 'contoh: /kunjungan' },
        { key: 'image_url', label: 'Gambar', placeholder: '', type: 'upload', collection: 'sections/services', recSize: '600×400px' },
        { key: 'long_description', label: 'HTML Detail (Opsional)', placeholder: '<p>...</p>', type: 'richtext' },
      ],
    },
    defaultContent: { title: '', subtitle: '', services: [] },
  },
  {
    key: 'products', label: 'Produk', icon: '📦',
    desc: 'Katalog produk dalam grid.',
    tip: 'Nama, deskripsi, harga, badge, gambar 400×400px.',
    fields: [
      { key: 'title', label: 'Judul Bagian', placeholder: 'contoh: Produk Pembersih Premium' },
      { key: 'subtitle', label: 'Subjudul', placeholder: 'Formula hydro vacuum...' },
    ],
    arrayField: {
      key: 'products', label: 'Produk', singular: 'produk',
      fields: [
        { key: 'name', label: 'Nama Produk', placeholder: 'contoh: All-Purpose Cleaner 1L' },
        { key: 'description', label: 'Deskripsi', placeholder: '1-2 kalimat', type: 'textarea' },
        { key: 'price', label: 'Harga', placeholder: 'contoh: Rp 45.000' },
        { key: 'badge', label: 'Badge', placeholder: 'contoh: Best Seller' },
        { key: 'icon', label: 'Icon', type: 'icon' },
        { key: 'image_url', label: 'Gambar Produk', placeholder: '', type: 'upload', collection: 'sections/products', recSize: '400×400px' },
      ],
    },
    defaultContent: { title: '', subtitle: '', products: [] },
  },
  {
    key: 'testimonials', label: 'Testimoni', icon: '💬',
    desc: 'Testimoni pelanggan dalam grid.',
    tip: '3-6 testimoni: nama, peran, teks, rating 1-5.',
    fields: [
      { key: 'title', label: 'Judul Bagian', placeholder: 'contoh: Apa Kata Pelanggan?' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
    ],
    arrayField: {
      key: 'testimonials', label: 'Testimoni', singular: 'testimoni',
      fields: [
        { key: 'name', label: 'Nama', placeholder: 'contoh: Dian Hartini' },
        { key: 'role', label: 'Peran', placeholder: 'contoh: Ibu Rumah Tangga, Jakarta' },
        { key: 'text', label: 'Isi Testimoni', placeholder: 'Tulis testimoni di sini...', type: 'textarea' },
        { key: 'rating', label: 'Rating (1-5)', placeholder: '5', type: 'number' },
      ],
    },
    defaultContent: { title: '', subtitle: '', testimonials: [] },
  },
  {
    key: 'cta', label: 'CTA', icon: '🔔',
    desc: 'Call-to-Action — ajakan bertindak.',
    tip: 'Judul memotivasi, subjudul pendukung, 1 tombol.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Siap Rasakan Kebersihan Premium?' },
      { key: 'subtitle', label: 'Subjudul', placeholder: 'Dapatkan konsultasi gratis...' },
      { key: 'cta_text', label: 'Teks Tombol', placeholder: 'contoh: Hubungi Kami' },
      { key: 'cta_link', label: 'Link Tombol', placeholder: 'contoh: /contact' },
      { key: 'background_color', label: 'Warna Background', placeholder: 'contoh: #0B1F3A', hint: 'hex color code' },
    ],
    defaultContent: { title: '', subtitle: '', cta_text: '', cta_link: '', background_color: '' },
  },
  {
    key: 'overview', label: 'Company Overview', icon: '🏢',
    desc: 'Gambaran umum perusahaan.',
    tip: 'Deskripsi 2-3 paragraf + gambar 600×400px + highlight.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Tentang Klinnovac' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
      { key: 'description', label: 'Deskripsi', placeholder: 'Ceritakan tentang perusahaan...', type: 'richtext' },
      { key: 'image_url', label: 'Gambar', placeholder: '', type: 'upload', collection: 'sections/overview', recSize: '600×400px' },
    ],
    arrayField: {
      key: 'features', label: 'Highlight Fitur', singular: 'highlight',
      fields: [
        { key: 'title', label: 'Judul', placeholder: 'contoh: Teknologi Terkini' },
        { key: 'description', label: 'Deskripsi', placeholder: 'Singkat', type: 'textarea' },
      ],
    },
    defaultContent: { title: '', subtitle: '', description: '', image_url: '', features: [] },
  },
  {
    key: 'vision_mission', label: 'Visi & Misi', icon: '🎯',
    desc: 'Visi dan misi perusahaan.',
    tip: 'Visi 1 kalimat inspiratif. Misi 3-5 poin.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Visi & Misi Klinnovac' },
      { key: 'vision', label: 'Visi', placeholder: 'contoh: Menjadi penyedia layanan kebersihan premium #1 di Indonesia' },
      { key: 'vision_description', label: 'Deskripsi Visi', placeholder: 'Ceritakan lebih detail...', type: 'textarea' },
      { key: 'mission', label: 'Misi', placeholder: 'contoh: Misi Kami' },
      { key: 'mission_description', label: 'Deskripsi Misi', placeholder: 'Ceritakan lebih detail...', type: 'textarea' },
    ],
    arrayField: {
      key: 'items', label: 'Poin Misi', singular: 'poin',
      fields: [
        { key: 'value', label: 'Poin', placeholder: 'contoh: Menghadirkan teknologi hydro vacuum terkini' },
      ],
    },
    defaultContent: { title: '', vision: '', vision_description: '', mission: '', mission_description: '', items: [] },
  },
  {
    key: 'timeline', label: 'Timeline', icon: '📅',
    desc: 'Perjalanan kronologis.',
    tip: '4-8 events: tahun, judul, deskripsi.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Perjalanan Klinnovac' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
    ],
    arrayField: {
      key: 'events', label: 'Event', singular: 'event',
      fields: [
        { key: 'year', label: 'Tahun', placeholder: 'contoh: 2018' },
        { key: 'title', label: 'Judul Event', placeholder: 'contoh: Didirikan di Bekasi' },
        { key: 'description', label: 'Deskripsi', placeholder: 'Singkat saja...', type: 'textarea' },
      ],
    },
    defaultContent: { title: '', subtitle: '', events: [] },
  },
  {
    key: 'core_values', label: 'Nilai Inti', icon: '💎',
    desc: 'Nilai-nilai inti perusahaan.',
    tip: '4-6 nilai: icon (1-6), judul, deskripsi.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Nilai Inti Kami' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
    ],
    arrayField: {
      key: 'values', label: 'Nilai', singular: 'nilai',
      fields: [
        { key: 'title', label: 'Judul', placeholder: 'contoh: Integritas' },
        { key: 'description', label: 'Deskripsi', placeholder: 'Singkat', type: 'textarea' },
        { key: 'icon', label: 'Icon', type: 'icon' },
      ],
    },
    defaultContent: { title: '', subtitle: '', values: [] },
  },
  {
    key: 'team', label: 'Tim', icon: '👥',
    desc: 'Anggota tim dengan foto dan bio.',
    tip: '3-8 anggota: nama, jabatan, foto, bio.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Tim Kami' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
    ],
    arrayField: {
      key: 'members', label: 'Anggota', singular: 'anggota',
      fields: [
        { key: 'name', label: 'Nama', placeholder: 'contoh: Ahmad Fauzi' },
        { key: 'position', label: 'Jabatan', placeholder: 'contoh: CEO & Founder' },
        { key: 'bio', label: 'Bio Singkat', placeholder: '', type: 'textarea' },
        { key: 'image_url', label: 'Foto', placeholder: '', type: 'upload', collection: 'sections/team', recSize: '200×200px' },
        { key: 'avatar', label: 'Avatar (emoji fallback)', placeholder: 'contoh: 👤' },
      ],
    },
    defaultContent: { title: '', subtitle: '', members: [] },
  },
  {
    key: 'stats', label: 'Statistik', icon: '📊',
    desc: 'Angka pencapaian dalam grid.',
    tip: '3-4 statistik: label, angka, suffix.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Klinnovac dalam Angka' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
      { key: 'background_color', label: 'Warna Background', placeholder: 'contoh: #f0fdf4' },
    ],
    arrayField: {
      key: 'stats', label: 'Statistik', singular: 'statistik',
      fields: [
        { key: 'label', label: 'Label', placeholder: 'contoh: Pelanggan Puas' },
        { key: 'value', label: 'Angka', placeholder: 'contoh: 10000' },
        { key: 'suffix', label: 'Suffix', placeholder: 'contoh: +' },
      ],
    },
    defaultContent: { title: '', subtitle: '', stats: [], background_color: '' },
  },
  {
    key: 'content', label: 'Konten HTML', icon: '📝',
    desc: 'Konten bebas dalam format HTML.',
    tip: 'Gunakan h2, h3, p, ul, ol, strong. Hindari inline style.',
    fields: [
      { key: 'body', label: 'Konten HTML', placeholder: '<h2>Judul</h2><p>Isi...</p>', type: 'richtext' },
    ],
    defaultContent: { body: '' },
  },
  {
    key: 'gallery', label: 'Galeri', icon: '🖼️',
    desc: 'Kumpulan gambar dalam grid.',
    tip: 'Gambar persegi (1:1), rekomendasi 800×800px.',
    fields: [
      { key: 'title', label: 'Judul', placeholder: 'contoh: Galeri Klinnovac' },
      { key: 'subtitle', label: 'Subjudul', placeholder: '' },
    ],
    defaultContent: { title: '', subtitle: '', images: [] },
  },
];

const getTypeDef = (key) => ALL_TYPES.find(t => t.key === key) || ALL_TYPES[0];

/* ============================================================
   Icon Name -> React Component helper
   ============================================================ */
function RenderIcon({ name, size = 18, className = '' }) {
  if (!name) return null;
  const Icon = LucideIcons[name];
  if (!Icon) {
    // Fallback for emoji-style strings
    if (typeof name === 'string' && name.length <= 4) return <span className={className} style={{ fontSize: size }}>{name}</span>;
    return null;
  }
  return <Icon size={size} className={className} />;
}

/* ============================================================
   Drag & Drop Array Item Editor
   ============================================================ */
function ArrayItemCard({ typeDef, items, onChange }) {
  if (!typeDef.arrayField) return null;

  const af = typeDef.arrayField;
  const add = () => {
    const empty = {};
    af.fields.forEach(f => {
      empty[f.key] = f.type === 'number' ? 1 : '';
    });
    onChange([...items, empty]);
  };

  const update = (index, key, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [key]: value };
    onChange(updated);
  };

  const remove = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(items);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    onChange(reordered);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-gray-700">{af.label} <span className="text-xs font-normal text-gray-400">({items.length} {af.singular})</span></h4>
        <button type="button" onClick={add} className="text-xs bg-white text-primary border border-primary px-3 py-1.5 rounded-lg hover:bg-primary hover:text-white transition-colors font-semibold">
          + Tambah {af.singular}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm bg-white rounded-lg border border-dashed border-gray-200">
          Belum ada {af.singular}. Klik <strong>+ Tambah {af.singular}</strong> di atas.
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`array-${af.key}`}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                {items.map((item, idx) => (
                  <Draggable key={idx} draggableId={`${af.key}-${idx}`} index={idx}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`bg-white rounded-xl border border-gray-100 p-4 shadow-sm transition-shadow ${
                          snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded flex items-center gap-1">
                            <span {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" title="Tarik untuk mengurutkan">⠿</span>
                            #{idx + 1} {item.name || item.title || ''}
                          </span>
                          <button type="button" onClick={() => remove(idx)} className="text-red-400 hover:text-red-600 px-1 text-xs font-bold" title="Hapus">✕</button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {af.fields.map(f => {
                            const val = item[f.key] !== undefined ? item[f.key] : '';

                            if (f.type === 'icon') {
                              return (
                                <div key={f.key} className="sm:col-span-2">
                                  <LucideIconPicker
                                    label={f.label}
                                    value={val}
                                    onChange={v => update(idx, f.key, v)}
                                  />
                                </div>
                              );
                            }

                            if (f.type === 'upload') {
                              return (
                                <div key={f.key} className={f.key === 'image_url' ? 'sm:col-span-2' : ''}>
                                  <ImageUploader
                                    label={f.label}
                                    value={val}
                                    onChange={v => update(idx, f.key, v)}
                                    collection={f.collection || 'sections'}
                                    recommendedSize={f.recSize}
                                  />
                                </div>
                              );
                            }

                            if (f.type === 'richtext') {
                              return (
                                <div key={f.key} className="sm:col-span-2">
                                  <RichTextEditor
                                    label={f.label}
                                    value={val}
                                    onChange={v => update(idx, f.key, v)}
                                    height={250}
                                    placeholder={f.placeholder}
                                  />
                                </div>
                              );
                            }

                            return (
                              <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">
                                  {f.label}
                                  {f.hint && <span className="text-gray-400 font-normal ml-1">({f.hint})</span>}
                                </label>
                                {f.type === 'textarea' ? (
                                  <textarea
                                    rows={3}
                                    value={val}
                                    onChange={e => update(idx, f.key, e.target.value)}
                                    placeholder={f.placeholder}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none resize-vertical"
                                  />
                                ) : f.type === 'number' ? (
                                  <input
                                    type="number"
                                    value={val}
                                    onChange={e => update(idx, f.key, e.target.value)}
                                    placeholder={f.placeholder}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={val}
                                    onChange={e => update(idx, f.key, e.target.value)}
                                    placeholder={f.placeholder}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}

/* ============================================================
   Live Preview
   ============================================================ */
function SectionPreview({ typeKey, content }) {
  switch (typeKey) {
    case 'hero':
      return (
        <div style={{ background: 'linear-gradient(135deg, #081626 0%, #00CFFF 100%)', padding: '24px 16px', borderRadius: 12, textAlign: 'center', color: 'white' }}>
          {content.badge_text && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '3px 12px', borderRadius: 20, fontSize: 10, display: 'inline-block', marginBottom: 8 }}>{content.badge_text}</span>}
          <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{content.title || 'Judul Hero'}</h3>
          <p style={{ opacity: 0.85, fontSize: 11, marginBottom: 12 }}>{content.subtitle || 'Subjudul'}</p>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            {content.cta_text && <span style={{ background: '#66E0FF', color: '#081626', padding: '6px 16px', borderRadius: 20, fontWeight: 600, fontSize: 11 }}>{content.cta_text}</span>}
            {content.cta_secondary_text && <span style={{ border: '1px solid rgba(255,255,255,0.5)', padding: '6px 16px', borderRadius: 20, fontWeight: 600, fontSize: 11 }}>{content.cta_secondary_text}</span>}
          </div>
        </div>
      );
    case 'features':
      return (
        <div style={{ padding: 16, background: '#f9fafb', borderRadius: 12 }}>
          {content.title && <h4 style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{content.title}</h4>}
          {content.subtitle && <p style={{ textAlign: 'center', fontSize: 10, color: '#6b7280', marginBottom: 10 }}>{content.subtitle}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {(content.features || []).slice(0, 4).map((f, i) => (
              <div key={i} style={{ background: 'white', padding: 10, borderRadius: 8, textAlign: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}>
                  <RenderIcon name={f.icon} />
                </div>
                <div style={{ fontWeight: 600, fontSize: 10 }}>{f.title || 'Fitur'}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'services':
      return (
        <div style={{ padding: 16, background: '#f9fafb', borderRadius: 12 }}>
          {content.title && <h4 style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{content.title}</h4>}
          {content.subtitle && <p style={{ textAlign: 'center', fontSize: 10, color: '#6b7280', marginBottom: 10 }}>{content.subtitle}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(content.services || []).slice(0, 3).map((s, i) => (
              <div key={i} style={{ background: 'white', padding: 8, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <RenderIcon name={s.icon} className="text-primary shrink-0" />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 11 }}>{s.name || 'Layanan'}</span>
                    {s.badge && <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '1px 6px', borderRadius: 8, fontSize: 8, fontWeight: 600 }}>{s.badge}</span>}
                  </div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>{s.short_description || ''}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 11, color: '#059669' }}>{s.price || ''}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'products':
      return (
        <div style={{ padding: 16, background: '#f9fafb', borderRadius: 12 }}>
          {content.title && <h4 style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{content.title}</h4>}
          {content.subtitle && <p style={{ textAlign: 'center', fontSize: 10, color: '#6b7280', marginBottom: 10 }}>{content.subtitle}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {(content.products || []).slice(0, 4).map((p, i) => (
              <div key={i} style={{ background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <div style={{ height: 50, background: '#e0f2f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RenderIcon name={p.icon} /></div>
                <div style={{ padding: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 9 }}>{p.name || 'Produk'}</div>
                  <div style={{ fontSize: 8, color: '#6b7280' }}>{p.description || ''}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 10, color: '#059669' }}>{p.price || ''}</span>
                    {p.badge && <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 4px', borderRadius: 6, fontSize: 7, fontWeight: 600 }}>{p.badge}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'testimonials':
      return (
        <div style={{ padding: 16, background: '#f9fafb', borderRadius: 12 }}>
          {content.title && <h4 style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{content.title}</h4>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(content.testimonials || []).slice(0, 3).map((t, i) => (
              <div key={i} style={{ background: 'white', padding: 10, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <div style={{ color: '#f59e0b', fontSize: 10, marginBottom: 2 }}>{'⭐'.repeat(Number(t.rating) || 5)}</div>
                <div style={{ fontSize: 9, color: '#374151', marginBottom: 4, fontStyle: 'italic' }}>"{t.text || ''}"</div>
                <div style={{ fontWeight: 600, fontSize: 9 }}>{t.name || ''}</div>
                <div style={{ fontSize: 8, color: '#6b7280' }}>{t.role || ''}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'cta':
      return (
        <div style={{ padding: 20, borderRadius: 12, textAlign: 'center', color: 'white', background: content.background_color || '#081626' }}>
          <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{content.title || 'CTA'}</h4>
          <p style={{ opacity: 0.85, fontSize: 10, marginBottom: 10 }}>{content.subtitle || ''}</p>
          {content.cta_text && <span style={{ background: '#66E0FF', color: '#081626', padding: '6px 18px', borderRadius: 20, fontWeight: 600, fontSize: 11 }}>{content.cta_text}</span>}
        </div>
      );
    default:
      return <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 11 }}>Preview untuk tipe <strong>{typeKey}</strong></div>;
  }
}

/* ============================================================
   SectionTypeIcon for table
   ============================================================ */
const SectionTypeIcon = ({ type }) => {
  const def = getTypeDef(type);
  return <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs flex items-center gap-1 w-fit"><span>{def.icon}</span>{def.label}</span>;
};

/* ============================================================
   MAIN PAGE COMPONENT
   ============================================================ */
export default function SectionsPage() {
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 15 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formType, setFormType] = useState('hero');
  const [formPageId, setFormPageId] = useState('');
  const [formOrder, setFormOrder] = useState(0);
  const [formActive, setFormActive] = useState(true);
  const [formContent, setFormContent] = useState({});

  // --- Data fetching ---
  const fetchSections = async (page = 1) => {
    setLoading(true);
    try {
      const res = await sectionApi.list({ per_page: 15, page });
      const d = res.data.data;
      setSections(Array.isArray(d) ? d : d?.data || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0, per_page: d?.per_page || 15 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const fetchPages = async () => {
    try {
      const res = await pageApi.list({ per_page: 100 });
      setPages(Array.isArray(res.data.data) ? res.data.data : res.data.data?.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchSections(); fetchPages(); }, []);

  // --- Modal actions ---
  const openCreate = (typeKey = 'hero') => {
    const def = getTypeDef(typeKey);
    setEditingId(null);
    setFormName('');
    setFormSlug('');
    setFormType(typeKey);
    setFormPageId('');
    setFormOrder(0);
    setFormActive(true);
    setFormContent({ ...def.defaultContent });
    setShowModal(true);
  };

  const openEdit = (section) => {
    setEditingId(section.id);
    setFormName(section.name);
    setFormSlug(section.slug);
    setFormType(section.type);
    setFormPageId(section.page_id || '');
    setFormOrder(section.order);
    setFormActive(section.is_active === 1 || section.is_active === true);

    let parsed;
    try {
      parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
    } catch {
      parsed = {};
    }
    const def = getTypeDef(section.type);
    setFormContent({ ...def.defaultContent, ...parsed });
    setShowModal(true);
  };

  const handleTypeChange = (newType) => {
    const def = getTypeDef(newType);
    setFormType(newType);
    setFormContent({ ...def.defaultContent });
  };

  const updateField = (key, value) => {
    setFormContent(prev => ({ ...prev, [key]: value }));
  };

  const updateArrayField = (key, value) => {
    setFormContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formName,
        slug: formSlug,
        type: formType,
        content: JSON.stringify(formContent),
        is_active: formActive ? 1 : 0,
        order: parseInt(formOrder) || 0,
      };
      if (formPageId) payload.page_id = parseInt(formPageId);

      if (editingId) {
        await sectionApi.update(editingId, payload);
      } else {
        await sectionApi.create(payload);
      }
      setShowModal(false);
      fetchSections(pagination.current_page);
    } catch (err) {
      alert('Gagal menyimpan: ' + (err.response?.data?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus seksi ini?')) return;
    try {
      await sectionApi.delete(id);
      fetchSections(pagination.current_page);
    } catch (err) { alert('Gagal menghapus.'); }
  };

  // Drag & drop in table to reorder sections
  const onSectionsDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reordered = Array.from(sections);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Update orders locally
    const updated = reordered.map((s, idx) => ({ ...s, order: idx + 1 }));
    setSections(updated);

    // Save new order to backend (update order for each section)
    try {
      await Promise.all(
        updated.map((s, idx) =>
          sectionApi.update(s.id, { order: idx + 1, name: s.name, slug: s.slug, type: s.type, is_active: s.is_active ? 1 : 0 })
        )
      );
    } catch (err) {
      console.error('Gagal menyimpan urutan:', err);
      fetchSections(pagination.current_page); // revert
    }
  };

  // Pagination
  const pagesArr = [];
  for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) pagesArr.push(i);

  const currentTypeDef = getTypeDef(formType);
  const topFields = currentTypeDef.fields || [];

  // --- Render ---
  return (
    <AdminLayout active="Seksi">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 mb-1">Seksi</h1>
          <p className="text-sm text-gray-500 mt-1">Setiap seksi adalah satu blok konten di halaman depan. Pilih tipe, isi form, simpan.</p>
        </div>
        <button onClick={() => openCreate('hero')} className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors">
          + Tambah Seksi
        </button>
      </div>

      {/* Type selector grid */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Klik tipe untuk langsung membuat seksi baru:</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {ALL_TYPES.map(t => (
            <div
              key={t.key}
              onClick={() => openCreate(t.key)}
              className="bg-white border border-gray-100 rounded-xl p-3 text-center hover:shadow-md transition-shadow cursor-pointer hover:border-primary/30"
            >
              <span className="text-2xl block mb-1">{t.icon}</span>
              <span className="text-xs font-semibold text-gray-700">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : sections.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Belum ada seksi. Klik tipe di atas atau + Tambah Seksi.</div>
        ) : (
          <>
            <DragDropContext onDragEnd={onSectionsDragEnd}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-gray-500 w-8"></th>
                    <th className="px-4 py-3 font-medium text-gray-500">Nama</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Tipe</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Halaman</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Aktif</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Urutan</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Aksi</th>
                  </tr>
                </thead>
                <Droppable droppableId="sections-table">
                  {(provided) => (
                    <tbody {...provided.droppableProps} ref={provided.innerRef} className="divide-y divide-gray-50">
                      {sections.map((s, index) => (
                        <Draggable key={s.id} draggableId={`section-${s.id}`} index={index}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`hover:bg-gray-50 ${snapshot.isDragging ? 'bg-blue-50 shadow-lg' : ''}`}
                            >
                              <td className="px-4 py-4">
                                <span {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" title="Tarik untuk mengurutkan">⠿</span>
                              </td>
                              <td className="px-4 py-4 font-medium text-gray-900">{s.name}</td>
                              <td className="px-4 py-4"><SectionTypeIcon type={s.type} /></td>
                              <td className="px-4 py-4 text-gray-500">{s.page?.title || s.page_id || '-'}</td>
                              <td className="px-4 py-4">{s.is_active ? <span className="text-green-500">✅ Aktif</span> : <span className="text-gray-300">❌ Nonaktif</span>}</td>
                              <td className="px-4 py-4 text-gray-500">{s.order}</td>
                              <td className="px-4 py-4">
                                <div className="flex gap-2">
                                  <button onClick={() => openEdit(s)} className="text-primary hover:underline text-xs font-semibold">Edit</button>
                                  <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:underline text-xs">Hapus</button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </DragDropContext>
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">Total {pagination.total} data · Halaman {pagination.current_page} dari {pagination.last_page}</span>
                <div className="flex items-center gap-1">
                  <button disabled={pagination.current_page === 1} onClick={() => fetchSections(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">« Prev</button>
                  {pagesArr.map(p => (<button key={p} onClick={() => fetchSections(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>))}
                  <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchSections(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">Next »</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============ MODAL ============ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[94vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <span>{currentTypeDef.icon}</span> {editingId ? 'Edit Seksi' : 'Tambah Seksi'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">{currentTypeDef.desc}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleSave} className="space-y-5" id="section-form">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h4 className="text-sm font-bold text-gray-700 mb-3">📋 Informasi Dasar</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Nama Seksi</label>
                        <input type="text" required value={formName} onChange={e => setFormName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" placeholder="contoh: Hero Beranda" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Slug</label>
                        <input type="text" required value={formSlug} onChange={e => setFormSlug(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" placeholder="contoh: home-hero" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Tipe</label>
                        <select value={formType} onChange={e => handleTypeChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none">
                          {ALL_TYPES.map(t => <option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Urutan</label>
                        <input type="number" value={formOrder} onChange={e => setFormOrder(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Halaman</label>
                        <select value={formPageId} onChange={e => setFormPageId(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none">
                          <option value="">-- Tidak terkait halaman --</option>
                          {pages.map(p => <option key={p.id} value={p.id}>{p.title} (/{p.slug})</option>)}
                        </select>
                      </div>
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={formActive} onChange={e => setFormActive(e.target.checked)} className="w-4 h-4 text-primary rounded" />
                          <span className="text-sm font-medium text-gray-700">Aktif? (tampil di website)</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
                    <span className="text-blue-500 text-sm mt-0.5">💡</span>
                    <p className="text-xs text-blue-700"><strong>Tips:</strong> {currentTypeDef.tip}</p>
                  </div>

                  {topFields.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-100 p-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-3">📝 Isi Konten</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {topFields.map(f => {
                          const val = formContent[f.key] !== undefined ? formContent[f.key] : '';

                          if (f.type === 'icon') {
                            return (
                              <LucideIconPicker
                                key={f.key}
                                label={f.label}
                                value={val}
                                onChange={v => updateField(f.key, v)}
                              />
                            );
                          }

                          if (f.type === 'richtext') {
                            return (
                              <RichTextEditor key={f.key} label={f.label} value={val} onChange={v => updateField(f.key, v)} height={300} placeholder={f.placeholder} />
                            );
                          }

                          if (f.type === 'upload') {
                            return (
                              <ImageUploader key={f.key} label={f.label} value={val} onChange={v => updateField(f.key, v)} collection={f.collection || 'sections'} recommendedSize={f.recSize} />
                            );
                          }

                          return (
                            <div key={f.key}>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {f.label}
                                {f.hint && <span className="text-gray-400 font-normal text-xs ml-1">({f.hint})</span>}
                              </label>
                              {f.type === 'textarea' ? (
                                <textarea rows={4} value={val} onChange={e => updateField(f.key, e.target.value)} placeholder={f.placeholder} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none resize-vertical" />
                              ) : (
                                <input type="text" value={val} onChange={e => updateField(f.key, e.target.value)} placeholder={f.placeholder} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {currentTypeDef.arrayField && (
                    <ArrayItemCard typeDef={currentTypeDef} items={formContent[currentTypeDef.arrayField.key] || []} onChange={v => updateArrayField(currentTypeDef.arrayField.key, v)} />
                  )}

                  {formType === 'gallery' && (
                    <ArrayItemCard
                      typeDef={{
                        arrayField: {
                          key: 'images', label: 'Gambar Galeri', singular: 'gambar',
                          fields: [
                            { key: 'url', label: 'Gambar', placeholder: '', type: 'upload', collection: 'sections/gallery', recSize: '800×800px' },
                            { key: 'alt', label: 'Alt Text', placeholder: 'Deskripsi gambar' },
                          ],
                        },
                      }}
                      items={formContent.images || []}
                      onChange={v => updateArrayField('images', v)}
                    />
                  )}
                </form>
              </div>

              <div className="lg:w-80 xl:w-96 shrink-0 p-4 border-l border-gray-100 bg-gray-50 overflow-y-auto hidden lg:flex lg:flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-gray-700">👁️ Preview Langsung</span>
                  <span className="text-xs text-gray-400">— {currentTypeDef.label}</span>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <SectionPreview typeKey={formType} content={formContent} />
                </div>
                <p className="text-xs text-gray-400 mt-3 text-center leading-relaxed">
                  Preview ini diperbarui otomatis.<br />Tampilan asli mungkin sedikit berbeda.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 shrink-0 bg-white">
              <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-xl font-medium">Batal</button>
              <button type="submit" form="section-form" disabled={saving} className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-primary-dark disabled:opacity-50 transition-colors">{saving ? '⏳ Menyimpan...' : '💾 Simpan'}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}